import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getFindOrderConditionsKey } from '../../../services/modules/orderCondition/orderCondition.keys';
import { useFindOrderConditions } from '../../../services/modules/orderCondition/orderCondition.queries';
import { WithId } from '../../../types/Base.types';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import { GetOrderConditionDTO } from '../conditions/dtos/orderCondition.dto';
import { OrderCondition } from '../conditions/interfaces/orderCondition.interface';
import { sortConditions } from '../conditions/orderCondition.utils';
import useSelectedOrderCondition, {
  ALL_ORDER_CONDITIONS_ID,
} from './hooks/useSelectedOrderCondition';

interface OrderConditionContextProps {
  orderConditions: OrderCondition[];
  isLoadingOrderConditions: boolean;
  isErrorOrderConditions: boolean;
  selectedConditionId: string;
  selectedCondition?: OrderCondition;
  isAllConditionsSelected: boolean;
  selectCondition: (conditionId: string) => void;
  updateOrderCondition: (newOrderCondition: GetOrderConditionDTO) => void;
  invalidateOrderConditions: () => void;
}

export const OrderConditionContext = createContext<OrderConditionContextProps>({
  orderConditions: [],
  isLoadingOrderConditions: false,
  isErrorOrderConditions: false,
  selectedConditionId: ALL_ORDER_CONDITIONS_ID,
  isAllConditionsSelected: true,
  selectCondition: () => {
    return;
  },
  updateOrderCondition: () => {
    return;
  },
  invalidateOrderConditions: () => {
    return;
  },
});

interface OrderConditionContextProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}

export const OrderConditionContextProvider = ({
  children,
  isOfflineMode,
}: OrderConditionContextProviderProps) => {
  const params = useParams<WithId>();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();

  const orderConditionData = useFindOrderConditions({ orderId: params.id, isOfflineMode });

  const updateOrderCondition = useCallback(
    (newOrderCondition: GetOrderConditionDTO) => {
      queryClient.setQueryData<GetOrderConditionDTO[]>(
        [team?.id, ...getFindOrderConditionsKey({ orderId: params.id })],
        (oldOrderConditions?: GetOrderConditionDTO[]) => {
          if (!oldOrderConditions) {
            return oldOrderConditions;
          }

          return oldOrderConditions.map((orderCondition) => {
            if (orderCondition.id === newOrderCondition.id) {
              return { ...orderCondition, ...newOrderCondition };
            }

            return orderCondition;
          });
        },
      );
    },
    [params.id, queryClient, team?.id],
  );

  const orderConditions = useMemo(
    () =>
      orderConditionData?.data
        ? orderConditionData?.data.map(GetOrderConditionDTO.toState).sort(sortConditions)
        : [],
    [orderConditionData?.data],
  );

  const invalidateOrderConditions = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [team?.id, ...getFindOrderConditionsKey({ orderId: params.id })],
      }),
    [params.id, queryClient, team?.id],
  );

  const { selectedConditionId, selectedCondition, isAllConditionsSelected, selectCondition } =
    useSelectedOrderCondition({ orderConditions });

  const value: OrderConditionContextProps = {
    orderConditions,
    isLoadingOrderConditions: orderConditionData.isLoading,
    isErrorOrderConditions: orderConditionData.isError,
    selectedConditionId,
    selectedCondition,
    isAllConditionsSelected,
    selectCondition,
    updateOrderCondition,
    invalidateOrderConditions,
  };
  return <OrderConditionContext.Provider value={value}>{children}</OrderConditionContext.Provider>;
};

export const useOrderConditionContext = () =>
  useContext<OrderConditionContextProps>(OrderConditionContext);
